import Checkbox from "@mui/material/Checkbox";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MotorDataRow } from "../models/MotorDataRow";
import { MotorDataToolbar } from "./MotorDataToolbar";
import { I18nGrid } from "@/components/data/I18nGrid";

/** Row height the grid is configured with, used to size the grid to its content. */
const ROW_HEIGHT = 52;

interface Props {
  rows: MotorDataRow[];
  loading: boolean;
  /** Diagnostics currently charted. */
  chartedIds: Set<string>;
  onChartedChange: (diagnosticId: string, charted: boolean) => void;
}

/**
 * The measurements of a vehicle, grouped by what they are about.
 *
 * The grid is sized to its content and the page scrolls, rather than the grid scrolling inside a
 * fixed height: the groups are the point of the table, and a reader comparing two of them should
 * not have to scroll a pane inside a pane.
 */
export function MotorDataGrid({
  rows,
  loading,
  chartedIds,
  onChartedChange,
}: Props) {
  const { t } = useTranslation();

  const columns = useMemo<GridColDef<MotorDataRow>[]>(
    () => [
      {
        field: "chart",
        headerName: t("motorData.columns.chart", "Grafik"),
        width: 80,
        sortable: false,
        filterable: false,
        disableExport: true,
        renderCell: ({ row }: GridRenderCellParams<MotorDataRow>) => (
          <Checkbox
            size="small"
            checked={chartedIds.has(row.diagnosticId)}
            onChange={(_event, checked) => onChartedChange(row.diagnosticId, checked)}
            inputProps={{ "aria-label": t("motorData.columns.chart", "Grafik") }}
          />
        ),
      },
      {
        field: "latest",
        headerName: t("motorData.columns.latest", "Aktuell"),
        width: 130,
        sortable: false,
      },
      { field: "unit", headerName: t("motorData.columns.unit", "Einheit"), width: 110 },
      { field: "min", headerName: t("motorData.columns.min", "Minimum"), width: 120 },
      { field: "max", headerName: t("motorData.columns.max", "Maximum"), width: 120 },
      { field: "average", headerName: t("motorData.columns.average", "Mittelwert"), width: 120 },
      {
        field: "count",
        headerName: t("motorData.columns.count", "Messwerte"),
        type: "number",
        width: 110,
      },
      {
        field: "lastUpdated",
        headerName: t("motorData.columns.lastUpdated", "Zuletzt gemeldet"),
        width: 170,
      },
      { field: "source", headerName: t("motorData.columns.source", "Quelle"), width: 180 },
      {
        field: "diagnosticId",
        headerName: t("motorData.columns.diagnosticId", "Diagnose-ID"),
        width: 220,
        sortable: false,
      },
    ],
    [t, chartedIds, onChartedChange],
  );

  const height = useMemo(() => {
    const groups = new Set(rows.map(row => row.groupPath[0])).size;
    // Header and footer chrome plus a row per measurement and per group.
    return Math.max(320, 110 + (rows.length + groups) * ROW_HEIGHT);
  }, [rows]);

  return (
    <I18nGrid
      rows={rows}
      columns={columns}
      loading={loading}
      getRowId={row => row.id}
      treeData
      getTreeDataPath={row => row.groupPath}
      groupingColDef={{
        headerName: t("motorData.columns.metric", "Messwert"),
        width: 360,
      }}
      defaultGroupingExpansionDepth={-1}
      disableRowSelectionOnClick
      disableDensitySelector
      slots={{ toolbar: MotorDataToolbar }}
      initialState={{
        columns: { columnVisibilityModel: { diagnosticId: false } },
        pagination: { paginationModel: { pageSize: 100, page: 0 } },
      }}
      pageSizeOptions={[50, 100, 250]}
      sx={{
        // The grid defaults to filling its parent, which ignores an explicit height; pin it to
        // its own size so the page scrolls instead of the grid.
        flex: "0 0 auto",
        height,
        "& .MuiDataGrid-row--group > .MuiDataGrid-cell": {
          bgcolor: "action.selected",
          fontWeight: 700,
        },
        "& .MuiDataGrid-treeDataGroupingCell": { fontWeight: 700 },
      }}
    />
  );
}
